import React, {
    useEffect,
    useMemo,
    useState,
} from 'react';

import { DataGrid } from '@mui/x-data-grid';

import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';

import {
    InfoOutlined,
} from '@mui/icons-material';

import {
    useGetDomainBreakdownRangeQuery,
} from '../../../../features/data_assets/dataAssetsApiSlice';

import {
    useGetOrganizationsQuery,
} from '../../../../features/organizations/organizationsApiSlice';

import {
    useDiscreteMode,
} from '../../global/DiscreteMode';

import { tokens } from '../../../../theme';


const DomainAnalytics = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { isDiscreteMode } = useDiscreteMode();

    const [rangeDays, setRangeDays] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedOrgPids, setSelectedOrgPids] = useState([]);

    const [
        orgSelectionInitialized,
        setOrgSelectionInitialized,
    ] = useState(false);


    // ---------------------------------------------------------
    // API data
    // ---------------------------------------------------------

    const {
        data: domainData,
        isLoading: isLoadingDomainData,
        isFetching: isFetchingDomainData,
    } = useGetDomainBreakdownRangeQuery(
        rangeDays,
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const {
        data: orgData,
        isLoading: isLoadingOrganizations,
    } = useGetOrganizationsQuery(
        'organizations',
        {
            pollingInterval: 15000,
            refetchOnFocus: true,
            refetchOnMountOrArgChange: true,
        }
    );


    // ---------------------------------------------------------
    // Organizations
    // ---------------------------------------------------------

    const orgArray = useMemo(
        () =>
            Object.values(
                orgData?.entities || {}
            ),
        [orgData?.entities]
    );

    const adcEnabledOrgs = useMemo(
        () =>
            orgArray
                .filter(
                    (org) => org.adc_enabled
                )
                .sort(
                    (a, b) =>
                        a.name.localeCompare(
                            b.name
                        )
                ),
        [orgArray]
    );

    const orgByPid = useMemo(
        () =>
            new Map(
                adcEnabledOrgs.map(
                    (org) => [
                        org.pid,
                        org,
                    ]
                )
            ),
        [adcEnabledOrgs]
    );


    // Default to ALL organizations once organizations load.
    useEffect(() => {
        if (
            !orgSelectionInitialized &&
            adcEnabledOrgs.length > 0
        ) {
            setSelectedOrgPids(
                adcEnabledOrgs.map(
                    (org) => org.pid
                )
            );

            setOrgSelectionInitialized(
                true
            );
        }
    }, [
        adcEnabledOrgs,
        orgSelectionInitialized,
    ]);

    const selectedOrgObjects = useMemo(
        () =>
            adcEnabledOrgs.filter(
                (org) =>
                    selectedOrgPids.includes(
                        org.pid
                    )
            ),
        [
            adcEnabledOrgs,
            selectedOrgPids,
        ]
    );


    // ---------------------------------------------------------
    // Domain data
    // ---------------------------------------------------------

    const domainRecords = useMemo(
        () =>
            (
                domainData?.records || []
            ).map((record) => {
                const org =
                    orgByPid.get(
                        record.partnerId
                    );

                return {
                    ...record,
                    organization:
                        org?.name ||
                        'Unknown',
                };
            }),
        [
            domainData?.records,
            orgByPid,
        ]
    );

    const selectedOrgDomainRecords = useMemo(
        () =>
            domainRecords.filter(
                (record) =>
                    selectedOrgPids.includes(
                        record.partnerId
                    )
            ),
        [
            domainRecords,
            selectedOrgPids,
        ]
    );

    const filteredDomains = useMemo(
        () =>
            selectedOrgDomainRecords.filter(
                (record) =>
                    !searchTerm ||
                    record.domain
                        ?.toLowerCase()
                        .includes(
                            searchTerm.toLowerCase()
                        )
            ),
        [
            selectedOrgDomainRecords,
            searchTerm,
        ]
    );

    const selectedDistinctDomainCount = useMemo(
        () =>
            new Set(
                selectedOrgDomainRecords
                    .map(
                        (record) =>
                            record.domain
                    )
                    .filter(Boolean)
            ).size,
        [selectedOrgDomainRecords]
    );


    // ---------------------------------------------------------
    // Organization controls
    // ---------------------------------------------------------

    const handleSelectAllOrgs = () => {
        setSelectedOrgPids(
            adcEnabledOrgs.map(
                (org) => org.pid
            )
        );
    };

    const handleDeselectAllOrgs = () => {
        setSelectedOrgPids([]);
    };


    // ---------------------------------------------------------
    // Grid
    // ---------------------------------------------------------

    const columns = [
        {
            field: 'domain',
            headerName: 'Main Domain',
            flex: 1,
        },
        {
            field: 'organization',
            headerName: 'Organization',
            flex: 1,
            renderCell: (params) =>
                isDiscreteMode
                    ? 'Confidential'
                    : params.value,
        },
        {
            field: 'totalCount',
            headerName: 'Occurrences',
            type: 'number',
            sortable: true,
        },
    ];

    const rows = filteredDomains.map(
        (item) => ({
            id: `${item.partnerId}-${item.domain}`,
            domain: item.domain,
            organization:
                item.organization,
            totalCount:
                item.totalCount,
        })
    );

    const isLoading =
        isLoadingDomainData ||
        isLoadingOrganizations;

    return (
        <Box sx={{ p: 2 }}>
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    <Grid
                        container
                        spacing={3}
                        alignItems="stretch"
                        sx={{ mb: 3 }}
                    >
                        {/* ---------------------------------------------------------
        Domain summary
       --------------------------------------------------------- */}
                        <Grid item xs={12} md={4}>
                            <Paper
                                sx={{
                                    p: 2,
                                    height: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        mb: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        color="text.secondary"
                                    >
                                        Distinct Main Domains
                                    </Typography>

                                    <Tooltip
                                        arrow
                                        placement="right"
                                        title={
                                            <Box sx={{ p: 0.5 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ mb: 1 }}
                                                >
                                                    Subdomains are rolled up into
                                                    their main registrable domain.
                                                </Typography>

                                                <Box
                                                    component="pre"
                                                    sx={{
                                                        m: 0,
                                                        fontSize: '0.75rem',
                                                        whiteSpace: 'pre',
                                                    }}
                                                >
                                                    {`www.splunk.com      → splunk.com
foo.splunk.com      → splunk.com
www.example.co.uk   → example.co.uk
a.b.example.co.uk   → example.co.uk`}
                                                </Box>
                                            </Box>
                                        }
                                    >
                                        <IconButton
                                            size="small"
                                            aria-label="What does main domain mean?"
                                        >
                                            <InfoOutlined
                                                fontSize="small"
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Typography variant="h3">
                                    {selectedDistinctDomainCount
                                        .toLocaleString()}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {domainData?.startDate}
                                    {' → '}
                                    {domainData?.endDate}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Across {
                                        selectedOrgPids.length
                                    } selected organization{
                                        selectedOrgPids.length === 1
                                            ? ''
                                            : 's'
                                    }
                                </Typography>
                            </Paper>
                        </Grid>


                        {/* ---------------------------------------------------------
        Date range
       --------------------------------------------------------- */}
                        <Grid item xs={12} md={4}>
                            <Paper
                                sx={{
                                    p: 2,
                                    height: '100%',
                                }}
                            >
                                <FormControl fullWidth>
                                    <InputLabel>
                                        Date Range
                                    </InputLabel>

                                    <Select
                                        value={rangeDays}
                                        label="Date Range"
                                        onChange={(event) =>
                                            setRangeDays(
                                                Number(
                                                    event.target.value
                                                )
                                            )
                                        }
                                    >
                                        <MenuItem value={1}>
                                            Last Day
                                        </MenuItem>

                                        <MenuItem value={7}>
                                            Last 7 Days
                                        </MenuItem>

                                        <MenuItem value={30}>
                                            Last 30 Days
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                {isFetchingDomainData && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mt: 1,
                                        }}
                                    >
                                        <CircularProgress
                                            size={16}
                                        />

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Loading domain data...
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        </Grid>


                        {/* ---------------------------------------------------------
        Domain search
       --------------------------------------------------------- */}
                        <Grid item xs={12} md={4}>
                            <Paper
                                sx={{
                                    p: 2,
                                    height: '100%',
                                }}
                            >
                                <TextField
                                    label="Search Domains"
                                    placeholder="e.g. splunk.com"
                                    variant="outlined"
                                    fullWidth
                                    value={searchTerm}
                                    onChange={(event) =>
                                        setSearchTerm(
                                            event.target.value
                                        )
                                    }
                                />
                            </Paper>
                        </Grid>
                    </Grid>


                    <Paper
                        sx={{
                            p: 2,
                            mb: 3,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: {
                                    xs: 'stretch',
                                    md: 'center',
                                },
                                flexDirection: {
                                    xs: 'column',
                                    md: 'row',
                                },
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 0.25 }}
                                >
                                    Organizations
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {selectedOrgPids.length ===
                                        adcEnabledOrgs.length
                                        ? `All ${adcEnabledOrgs.length} organizations selected`
                                        : `${selectedOrgPids.length} of ${adcEnabledOrgs.length} organizations selected`}
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexShrink: 0,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={
                                        handleSelectAllOrgs
                                    }
                                    disabled={
                                        selectedOrgPids.length ===
                                        adcEnabledOrgs.length
                                    }
                                >
                                    Select All
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={
                                        handleDeselectAllOrgs
                                    }
                                    disabled={
                                        selectedOrgPids.length === 0
                                    }
                                >
                                    Deselect All
                                </Button>
                            </Box>
                        </Box>


                        <Autocomplete
                            multiple
                            disableCloseOnSelect

                            options={adcEnabledOrgs}

                            value={selectedOrgObjects}

                            isOptionEqualToValue={(
                                option,
                                value
                            ) =>
                                option.pid === value.pid
                            }

                            getOptionLabel={(org) =>
                                isDiscreteMode
                                    ? 'Confidential'
                                    : org.name
                            }

                            onChange={(
                                event,
                                newSelectedOrgs
                            ) => {
                                setSelectedOrgPids(
                                    newSelectedOrgs.map(
                                        (org) => org.pid
                                    )
                                );
                            }}

                            /*
                             * Don't render 116 separate chips.
                             * Render one compact selection summary.
                             */
                            renderTags={(value) => {
                                if (!value.length) {
                                    return null;
                                }

                                const allSelected =
                                    value.length ===
                                    adcEnabledOrgs.length;

                                return (
                                    <Chip
                                        size="small"
                                        label={
                                            allSelected
                                                ? `All ${value.length} organizations`
                                                : `${value.length} organizations selected`
                                        }
                                    />
                                );
                            }}

                            renderOption={(
                                props,
                                option,
                                { selected }
                            ) => (
                                <li
                                    {...props}
                                    key={option.pid}
                                    style={{
                                        ...props.style,
                                        backgroundColor: selected
                                            ? colors.greenAccent[900]
                                            : undefined,
                                    }}
                                >
                                    <Checkbox
                                        checked={selected}
                                        sx={{
                                            mr: 1,
                                            color: colors.grey[400],

                                            '&.Mui-checked': {
                                                color: colors.greenAccent[300],
                                            },
                                        }}
                                    />

                                    {isDiscreteMode
                                        ? 'Confidential'
                                        : option.name}
                                </li>
                            )}

                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filter Organizations"
                                    placeholder={
                                        selectedOrgPids.length ===
                                            adcEnabledOrgs.length
                                            ? 'Search organizations...'
                                            : 'Search or select organizations...'
                                    }
                                />
                            )}

                            sx={{
                                width: '100%',
                            }}
                        />
                    </Paper>



                    {/* Keep your existing pie chart here.
                        Feed it filteredDomains / domainData.records
                        exactly as before. */}

                    <Box
                        sx={{
                            height: 800,
                            width: '100%',
                        }}
                    >
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            pageSize={100}
                            rowsPerPageOptions={[
                                100,
                                200,
                                500,
                            ]}
                            disableSelectionOnClick

                            localeText={{
                                noRowsLabel:
                                    selectedOrgPids.length === 0
                                        ? 'No organizations selected'
                                        : 'No domains found for the current filters',
                            }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
};

export default DomainAnalytics;